import { Navigate, useLocation } from "react-router-dom";
import { type ReactNode, useEffect, useState } from "react";
import { useAuthStore } from "../../stores/authStore";
import type { PermissionKey } from "@repo/types";
import { usePermission } from "../../hooks/usePermission";
import apiClient from "../../api/client";

interface ProtectedRouteProps {
  children: ReactNode;
  /** If provided, user must have this permission (or be SUPER_ADMIN) */
  requiredPermission?: PermissionKey;
  /** If true, only SUPER_ADMIN can access */
  superAdminOnly?: boolean;
}

export function ProtectedRoute({
  children,
  requiredPermission,
  superAdminOnly = false,
}: ProtectedRouteProps) {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const location = useLocation();
  const { can, isSuperAdmin } = usePermission();

  // On page reload: isAuthenticated is restored from sessionStorage but accessToken
  // is not (memory-only). Bootstrap silently refreshes the token before rendering
  // any children — prevents API calls going out without a token.
  const [bootstrapping, setBootstrapping] = useState(
    isAuthenticated && !accessToken,
  );

  useEffect(() => {
    if (!isAuthenticated || accessToken) {
      setBootstrapping(false);
      return;
    }
    apiClient
      .post<{ data: { accessToken: string } }>("/auth/refresh")
      .then((res) => {
        setAccessToken(res.data.data.accessToken);
      })
      .catch(() => {
        clearAuth();
      })
      .finally(() => {
        setBootstrapping(false);
      });
  }, []); // intentionally runs once on mount only

  // Not logged in — send to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Waiting for silent token refresh on page reload
  if (bootstrapping) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-saas-bg">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Refresh failed and clearAuth was called — isAuthenticated is now false,
  // next render hits the !isAuthenticated guard above. Guard against the
  // brief window where bootstrapping just finished but store hasn't updated.
  if (!accessToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Super admin only route
  if (superAdminOnly && !isSuperAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-2">
          <p className="text-lg font-semibold text-saas-text">Access Denied</p>
          <p className="text-sm text-saas-muted">
            You don&apos;t have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  // Permission check — if user data not yet ready, show nothing (avoids redirect loop)
  if (requiredPermission) {
    if (!user) return null;
    if (!can(requiredPermission)) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-2">
            <p className="text-lg font-semibold text-saas-text">
              Access Denied
            </p>
            <p className="text-sm text-saas-muted">
              You don&apos;t have permission to view this page.
            </p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
